/* Shared reconciliation helper. LittleGProfile / nativeNoticeStore retain cache
 * ownership; Core is the only cloud authority. No auth, navigation or UI owner. */
(function (root) {
  'use strict';
  const BASE = 'https://api.glhksolution.com/v1/littleg/living-cv';
  const NOTICE = '2026-10-26';
  const CONTRACT = 'littleg_private_cv_sync_v1';
  const SUBJECT = /^littleg_[a-f0-9]{40}$/;
  const copy = value => JSON.parse(JSON.stringify(value));
  function create({ storage, session, request, changed = () => {} }) {
    let owner = '', epoch = 0, state = null, available = false, running = null, queue = Promise.resolve();
    const empty = subject => ({ version: 1, owner: subject, cursor: 0, authorized: false, hydrated: false,
      records: {}, pending: [], recovery: [] });
    const lock = operation => {
      const guarded = () => {
        const subject = owner, generation = epoch;
        if (!subject || !storage.exclusive) return operation();
        return storage.exclusive(subject, async () => {
          active(subject, generation);
          const cached = await storage.read(subject);
          active(subject, generation);
          if (cached) {
            if (cached.owner !== subject || cached.version !== 1) throw new Error('cache_owner_mismatch');
            state = cached;
          }
          return operation();
        });
      };
      const next = queue.then(guarded, guarded);
      queue = next.catch(() => {});
      return next;
    };
    function active(subject, generation) {
      if (!subject || owner !== subject || epoch !== generation || session() !== subject) {
        throw Object.assign(new Error('Account changed'), { code: 'session_changed' });
      }
    }
    async function persist() {
      // Storage must acknowledge durability before emitting Saved locally/pending.
      await storage.write(owner, copy(state));
      changed(snapshot());
    }
    function snapshot() {
      if (!state || !owner || session() !== owner) return { owner: null, available: false, authorized: false, records: [], pending: 0 };
      const records = copy(state.records);
      for (const pending of state.pending) {
        records[pending.value.record_id] = { ...records[pending.value.record_id], ...pending.value,
          created_at: records[pending.value.record_id]?.created_at || pending.saved_at,
          deleted_at: pending.value.deleted ? 'pending' : null, status: pending.status };
      }
      return { owner, available, authorized: state.authorized,
        records: Object.values(records), pending: state.pending.length,
        hydrated: state.hydrated === true, sync_error: state.sync_error || null,
        recovery: copy(state.recovery) };
    }
    async function api(subject, generation, suffix = '', options = {}) {
      active(subject, generation);
      const response = await request(BASE + suffix, options);
      active(subject, generation);
      const payload = await response.json();
      active(subject, generation);
      if (!response.ok || payload.success !== true) {
        if (response.status !== 409) { available = false; changed(snapshot()); }
        throw Object.assign(new Error(payload.error?.code || 'sync_failed'),
          { code: payload.error?.code || 'sync_failed', status: response.status });
      }
      return payload.data;
    }
    async function bind() {
      const subject = session();
      // Invalidate before awaiting a queued disk write or another principal's IO.
      const generation = ++epoch;
      owner = ''; state = null; available = false; changed(snapshot());
      if (!SUBJECT.test(subject || '')) return;
      await lock(async () => {
        if (generation !== epoch || session() !== subject) return;
        const cached = await storage.read(subject);
        if (generation !== epoch || session() !== subject) return;
        if (cached && (cached.version !== 1 || cached.owner !== subject)) throw new Error('cache_owner_mismatch');
        owner = subject; state = cached || empty(subject); changed(snapshot());
      });
      if (owner === subject && generation === epoch) void reconcile().catch(() => {});
    }
    async function authorize() {
      const subject = owner, generation = epoch;
      active(subject, generation);
      if (!available) await reconcile().catch(() => {});
      active(subject, generation);
      if (!available) throw Object.assign(new Error('Private sync is temporarily unavailable.'), { code: 'sync_unavailable' });
      await api(subject, generation, '/consent', { method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notice_version: NOTICE, authorize_private_sync: true }) });
      await lock(async () => { active(subject, generation); state.authorized = true; await persist(); });
      return reconcile();
    }
    async function revoke() {
      const subject = owner, generation = epoch;
      await api(subject, generation, '/consent', { method: 'DELETE' });
      await lock(async () => { active(subject, generation); state.authorized = false; await persist(); });
    }
    async function save(recordType, content, recordId, deleted = false, baseRevision) {
      const subject = owner, generation = epoch;
      const result = await lock(async () => {
        active(subject, generation);
        if (!state.authorized) throw new Error('private_sync_authorization_required');
        const id = recordId || crypto.randomUUID();
        if (baseRevision !== undefined && (state.records[id]?.revision || 0) !== baseRevision) {
          throw new Error('revision_changed_keep_draft');
        }
        if (state.pending.some(p => p.value.record_id === id && p.status === 'Conflict requires resolution')) {
          throw new Error('resolve_conflict_first');
        }
        const count = state.pending.filter(p => p.value.record_id === id).length;
        const value = { record_id: id, record_type: recordType, schema_version: 1,
          mutation_id: crypto.randomUUID(), expected_revision: (state.records[id]?.revision || 0) + count,
          content: copy(deleted ? {} : content), deleted };
        const previous = copy(state);
        state.pending.push({ value, status: 'Sync pending', saved_at: new Date().toISOString() });
        try { await persist(); } catch (error) { state = previous; throw error; }
        return id;
      });
      void reconcile().catch(() => {});
      return result;
    }
    async function sync(subject, generation) {
      const consent = await api(subject, generation, '/consent');
      if (consent?.available !== true || consent.contract !== CONTRACT || consent.schema_version !== 1 ||
        consent.notice_version !== NOTICE || typeof consent.authorized !== 'boolean') {
        available = false;
        throw Object.assign(new Error('Private sync is temporarily unavailable.'), { code: 'sync_unavailable' });
      }
      available = true;
      await lock(async () => { active(subject, generation); state.authorized = consent.authorized; await persist(); });
      if (!consent.authorized) return;
      const attempted = new Set();
      while (true) {
        let item;
        await lock(() => { active(subject, generation);
          item = copy(state.pending.find(p => p.status !== 'Conflict requires resolution' &&
            !attempted.has(p.value.mutation_id)) || null); });
        if (!item) break;
        attempted.add(item.value.mutation_id);
        try {
          const data = await api(subject, generation, '', { method: 'POST',
            headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(item.value) });
          await lock(async () => {
            active(subject, generation);
            state.records[data.record.record_id] = { ...data.record, status: 'Synced' };
            state.pending = state.pending.filter(p => p.value.mutation_id !== item.value.mutation_id);
            await persist();
          });
        } catch (error) {
          active(subject, generation);
          await lock(async () => {
            active(subject, generation);
            for (const pending of state.pending.filter(p => p.value.record_id === item.value.record_id)) {
              pending.status = error.status === 409 ? 'Conflict requires resolution' : 'Sync failed';
            }
            await persist();
          });
          if (error.status !== 409) throw error;
        }
      }
      let more = true;
      while (more) {
        const data = await api(subject, generation, '?cursor=' + state.cursor + '&limit=100');
        await lock(async () => {
          active(subject, generation);
          for (const record of data.changes) {
            if (!state.records[record.record_id] || state.records[record.record_id].revision <= record.revision) {
              state.records[record.record_id] = { ...record, status: 'Synced' };
            }
          }
          state.cursor = Math.max(state.cursor, data.cursor); await persist(); more = data.has_more;
        });
      }
      await lock(async () => { active(subject, generation); state.hydrated = true; state.sync_error = null; await persist(); });
    }
    function reconcile() {
      const subject = owner, generation = epoch;
      if (!subject || session() !== subject) return Promise.resolve();
      if (running?.epoch === generation) { running.again = true; return running.promise; }
      const run = { epoch: generation, promise: null, again: false };
      run.promise = (async () => {
        do { run.again = false; await sync(subject, generation); } while (run.again && epoch === generation);
      })().catch(async error => {
        if (owner === subject && epoch === generation && session() === subject) {
          await lock(async () => { active(subject, generation); available = false; state.sync_error = error.code || error.message || 'sync_failed'; await persist(); });
        }
        throw error;
      }).finally(() => { if (running === run) running = null; });
      running = run; return run.promise;
    }
    async function resolve(recordId, choice) {
      if (choice !== 'server' && choice !== 'local') throw new Error('explicit_resolution_required');
      const subject = owner, generation = epoch;
      await reconcile(); // fetch current server revision before an explicit choice
      await lock(async () => {
        active(subject, generation);
        const pending = state.pending.filter(p => p.value.record_id === recordId);
        if (!pending.some(p => p.status === 'Conflict requires resolution')) throw new Error('conflict_required');
        // Preserve original local content even when choosing the server version.
        state.recovery.push({ record_id: recordId, pending: copy(pending), recovered_at: new Date().toISOString() });
        state.pending = state.pending.filter(p => p.value.record_id !== recordId);
        if (choice === 'local') state.pending.push({ value: { ...pending.at(-1).value,
          expected_revision: state.records[recordId]?.revision || 0, mutation_id: crypto.randomUUID() },
          status: 'Sync pending', saved_at: new Date().toISOString() });
        await persist();
      });
      return reconcile();
    }
    return Object.freeze({ bind, authorize, revoke, save, reconcile, resolve, snapshot });
  }
  async function singletonId(subject, type) {
    if (!SUBJECT.test(subject) || !['profile', 'career'].includes(type)) throw new Error('invalid_singleton');
    const bytes = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(subject + ':' + type)));
    bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes.slice(0, 16), b => b.toString(16).padStart(2, '0')).join('');
    return hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) + '-' + hex.slice(16, 20) + '-' + hex.slice(20);
  }
  root.LittleGSyncEngine = Object.freeze({ create, singletonId, NOTICE });
})(globalThis);
